import { useState } from "react";
import { Pencil, Trash2 } from "lucide-react";
import { Botao, DialogoDeConfirmacao, cn } from "@base/ui";
import {
  useAtualizarComentarioReferencia,
  useCriarComentarioReferencia,
  useExcluirComentarioReferencia,
  useListarComentariosReferencia,
} from "../application/use-referencias";

const CHAVE_NOME = "renkerefs.comentario.autorNome";

function lerNomeSalvo(): string {
  try {
    return localStorage.getItem(CHAVE_NOME)?.trim() ?? "";
  } catch {
    return "";
  }
}

function salvarNome(nome: string): void {
  try {
    localStorage.setItem(CHAVE_NOME, nome);
  } catch {
    // ignore quota / private mode
  }
}

/**
 * Comentários internos da equipe sobre a referência (espelhados no ClickUp).
 * Conta única: cada pessoa digita o próprio nome ao comentar.
 */
export function SecaoComentariosReferencia({
  postId,
}: {
  readonly postId: string;
}) {
  const lista = useListarComentariosReferencia(postId);
  const criar = useCriarComentarioReferencia(postId);
  const atualizar = useAtualizarComentarioReferencia(postId);
  const excluir = useExcluirComentarioReferencia(postId);
  const [autorNome, definirAutorNome] = useState(lerNomeSalvo);
  const [rascunho, definirRascunho] = useState("");
  const [editandoId, definirEditandoId] = useState<string | null>(null);
  const [textoEdicao, definirTextoEdicao] = useState("");
  const [comentarioParaExcluir, definirComentarioParaExcluir] = useState<
    string | null
  >(null);

  const podeEnviar =
    autorNome.trim().length > 0 && rascunho.trim().length > 0 && !criar.isPending;

  const enviar = async () => {
    const texto = rascunho.trim();
    const nome = autorNome.trim();
    if (!texto || !nome || criar.isPending) return;
    await criar.mutateAsync({ texto, autorNome: nome });
    salvarNome(nome);
    definirRascunho("");
  };

  const salvarEdicao = async () => {
    if (!editandoId || !textoEdicao.trim() || atualizar.isPending) return;
    await atualizar.mutateAsync({
      comentarioId: editandoId,
      texto: textoEdicao.trim(),
    });
    definirEditandoId(null);
    definirTextoEdicao("");
  };

  const confirmarExclusao = async () => {
    if (!comentarioParaExcluir || excluir.isPending) return;
    await excluir.mutateAsync(comentarioParaExcluir);
    definirComentarioParaExcluir(null);
  };

  return (
    <section className="space-y-3">
      <h3 className="text-[11px] font-medium uppercase tracking-wider text-muted-foreground">
        Comentários da equipe
      </h3>

      {lista.isLoading ? (
        <p className="text-sm text-muted-foreground">Carregando…</p>
      ) : null}

      {lista.isError ? (
        <p className="text-sm text-destructive">
          Não foi possível carregar os comentários.
        </p>
      ) : null}

      <ul className="space-y-3">
        {(lista.data ?? []).map((comentario) => (
          <li
            key={comentario.id}
            className="rounded-xl border border-border/70 bg-muted/30 px-3 py-2.5"
          >
            <div className="mb-1 flex items-center justify-between gap-2">
              <p className="text-xs font-medium text-muted-foreground">
                {comentario.autorNome}
                <span className="mx-1.5 text-border">·</span>
                {formatarQuando(comentario.criadoEm)}
              </p>
              {comentario.podeEditar ? (
                <div className="flex items-center gap-1">
                  <button
                    type="button"
                    aria-label="Editar comentário"
                    className="grid size-7 cursor-pointer place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                    onClick={() => {
                      definirEditandoId(comentario.id);
                      definirTextoEdicao(comentario.texto);
                    }}
                  >
                    <Pencil className="size-3.5" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    aria-label="Excluir comentário"
                    className="grid size-7 cursor-pointer place-items-center rounded-lg text-muted-foreground transition-colors hover:bg-destructive/12 hover:text-destructive"
                    disabled={excluir.isPending}
                    onClick={() => definirComentarioParaExcluir(comentario.id)}
                  >
                    <Trash2 className="size-3.5" aria-hidden="true" />
                  </button>
                </div>
              ) : null}
            </div>

            {editandoId === comentario.id ? (
              <div className="space-y-2">
                <textarea
                  value={textoEdicao}
                  onChange={(e) => definirTextoEdicao(e.target.value)}
                  rows={3}
                  className={cn(
                    "w-full resize-y rounded-lg border border-input bg-background px-3 py-2",
                    "text-sm text-foreground outline-none focus-visible:ring-2 focus-visible:ring-ring",
                  )}
                />
                <div className="flex gap-2">
                  <Botao
                    tamanho="pequeno"
                    variante="primario"
                    disabled={atualizar.isPending || !textoEdicao.trim()}
                    onClick={() => void salvarEdicao()}
                  >
                    Salvar
                  </Botao>
                  <Botao
                    tamanho="pequeno"
                    variante="fantasma"
                    onClick={() => {
                      definirEditandoId(null);
                      definirTextoEdicao("");
                    }}
                  >
                    Cancelar
                  </Botao>
                </div>
              </div>
            ) : (
              <p className="whitespace-pre-wrap text-sm leading-relaxed text-foreground">
                {comentario.texto}
              </p>
            )}
          </li>
        ))}
      </ul>

      {(lista.data?.length ?? 0) === 0 && !lista.isLoading ? (
        <p className="text-sm text-muted-foreground">
          Nenhum comentário ainda. Anote insights da peça aqui.
        </p>
      ) : null}

      <div className="space-y-2">
        <input
          type="text"
          value={autorNome}
          onChange={(e) => definirAutorNome(e.target.value)}
          maxLength={80}
          placeholder="Seu nome"
          aria-label="Seu nome"
          className={cn(
            "w-full rounded-xl border border-input bg-background px-3 py-2.5",
            "text-sm text-foreground placeholder:text-muted-foreground",
            "outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        />
        <textarea
          value={rascunho}
          onChange={(e) => definirRascunho(e.target.value)}
          rows={3}
          placeholder="Escreva um comentário…"
          className={cn(
            "w-full resize-y rounded-xl border border-input bg-background px-3 py-2.5",
            "text-sm text-foreground placeholder:text-muted-foreground",
            "outline-none focus-visible:ring-2 focus-visible:ring-ring",
          )}
        />
        <Botao
          className="w-full"
          variante="secundario"
          disabled={!podeEnviar}
          onClick={() => void enviar()}
        >
          {criar.isPending ? "Enviando…" : "Comentar"}
        </Botao>
      </div>

      <DialogoDeConfirmacao
        aberto={comentarioParaExcluir !== null}
        titulo="Excluir comentário?"
        descricao="Essa ação remove o comentário da equipe e do ClickUp. Não dá para desfazer."
        rotuloConfirmar="Excluir"
        carregando={excluir.isPending}
        aoCancelar={() => {
          if (!excluir.isPending) definirComentarioParaExcluir(null);
        }}
        aoConfirmar={() => void confirmarExclusao()}
      />
    </section>
  );
}

function formatarQuando(iso: string): string {
  try {
    return new Intl.DateTimeFormat("pt-BR", {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}
